/**
 * `<MarketStatusPill>` — one pill, mapping **1:1 onto the API's market state
 * enum** (components.md §2.5).
 *
 * Eleven states. The UI never invents a status, never merges two, and never
 * shows one the API cannot emit. There is no `LOCKED` state and no freeze state:
 * trading runs to the expiry instant, so there is no pill for a lock that does
 * not happen — and `docs/03` Appendix C fails the build on either spelling.
 *
 * **This is a rendering of `domain/lifecycle.ts`, not a second copy of it.** The
 * switch below is exhaustive with no `default`, so adding a state to
 * `spec/openapi.yaml` breaks this file rather than falling through to a blank
 * chip in production.
 *
 * **`FIXING` and `RESOLVING` carry a determinate bar** driven by the server's
 * own `open_at_ms` / `resolve_eta_ms` — never a hardcoded 13-second animation,
 * and never an indeterminate shimmer over a bounded interval. `<Pill>` takes a
 * fraction and owns no timer; the fraction is computed here from the deadline
 * the server published.
 *
 * **The two are no longer simultaneous.** Until register §F24 market N's
 * `RESOLVING` window *was* market N+1's `FIXING` window, and both fractions came
 * from `determinateProgress` over the same ten seconds, so they filled in
 * lockstep. `FIXING(N+1)` now runs `[anchor(N+1) − 10s, anchor(N+1))` and ends
 * where `RESOLVING(N)` begins. What is simultaneous instead is `RESOLVING(N)`
 * and `OPEN(N+1)` — one resolving pill and one open pill, with exactly one of
 * them tradable.
 */

import { Glyph, Pill, type PillTone } from '@/components/primitives'
import { determinateProgress } from '@/domain/countdown'
import type { MarketState } from '@/domain/lifecycle'
import { formatCountdown } from '@/lib/format'

export interface MarketStatusPillProps {
  state: MarketState
  /** `UP`/`DOWN` on a resolved market, for the glyph the label carries. */
  outcome?: 'UP' | 'DOWN' | null
  /** Corrected server time. Never `Date.now()` (D9). */
  nowMs?: number
  /** `FIXING` counts to this. */
  openAtMs?: number | null
  /** `RESOLVING` counts to this. */
  resolveEtaMs?: number | null
  /** Where the bar's window began — `expiry_at_ms` for `RESOLVING`, and
   *  **`strike_window_start_ms`** for `FIXING`, which since §F24 is
   *  `anchor_ms − twap_window` rather than `anchor_ms`. Read it from the market
   *  payload; deriving it makes the bar zero-width the day the offset changes. */
  windowStartMs?: number | null
  /** The `STALE` treatment: the client knows it is not receiving updates. */
  muted?: boolean
  className?: string
}

interface Rendered {
  tone: PillTone
  label: string
  dot?: boolean
  glyph?: 'up' | 'down' | 'check' | null
  /** `undefined` for no bar; a fraction or `null` for one. */
  progress?: number | null
}

export function MarketStatusPill({
  state,
  outcome = null,
  nowMs = 0,
  openAtMs = null,
  resolveEtaMs = null,
  windowStartMs = null,
  muted,
  className,
}: MarketStatusPillProps) {
  const rendered = render(state, {
    outcome,
    nowMs,
    openAtMs,
    resolveEtaMs,
    windowStartMs,
  })

  return (
    <Pill
      tone={rendered.tone}
      {...(rendered.dot ? { dot: true } : {})}
      {...(rendered.progress === undefined ? {} : { progress: rendered.progress })}
      {...(muted ? { muted: true } : {})}
      {...(className ? { className } : {})}
    >
      {rendered.glyph ? <Glyph name={rendered.glyph} label={null} /> : null}
      {rendered.label}
    </Pill>
  )
}

interface Context {
  outcome: 'UP' | 'DOWN' | null
  nowMs: number
  openAtMs: number | null
  resolveEtaMs: number | null
  windowStartMs: number | null
}

/**
 * The table, exhaustive over `MarketState`.
 *
 * Exported because `/gallery` renders every state from it, which is the only
 * way an eleven-state enum stays reviewable.
 */
export function render(state: MarketState, ctx: Context): Rendered {
  switch (state) {
    case 'SCHEDULED':
      // Strike is genuinely NULL and the book rejects orders. Saying so beats
      // showing a zero.
      return { tone: 'neutral', label: 'SCHEDULED' }

    case 'FIXING': {
      const remaining = ctx.openAtMs === null ? null : Math.max(0, ctx.openAtMs - ctx.nowMs)
      return {
        tone: 'info',
        label: remaining === null ? 'FIXING' : `FIXING · ${formatCountdown(remaining)}s`,
        progress: determinateProgress(ctx.windowStartMs, ctx.openAtMs, ctx.nowMs) ?? null,
      }
    }

    case 'OPEN':
      return { tone: 'up', label: 'OPEN', dot: true }

    case 'EXPIRED':
      // Sub-second and almost never seen. It exists so the client never renders
      // a gap between the last tick and `RESOLVING`.
      return { tone: 'neutral', label: 'EXPIRED' }

    case 'RESOLVING': {
      const remaining = ctx.resolveEtaMs === null ? null : Math.max(0, ctx.resolveEtaMs - ctx.nowMs)
      return {
        tone: 'info',
        label: remaining === null ? 'RESOLVING' : `RESOLVING · ${formatCountdown(remaining)}s`,
        // `null` — indeterminate — is the one honest render once
        // `resolve_eta_ms` has passed with no `RESOLVED`: inventing progress the
        // server has not reported would be a lie.
        progress: determinateProgress(ctx.windowStartMs, ctx.resolveEtaMs, ctx.nowMs) ?? null,
      }
    }

    case 'RESOLVING_DEGRADED':
      // It will still settle. The warning tone says the coverage was thin, not
      // that anything failed.
      return {
        tone: 'warning',
        label: 'RESOLVING · DEGRADED',
        progress: determinateProgress(ctx.windowStartMs, ctx.resolveEtaMs, ctx.nowMs) ?? null,
      }

    case 'RESOLVED':
      return {
        tone: ctx.outcome === 'DOWN' ? 'down' : 'up',
        label: `RESOLVED ${ctx.outcome ?? 'UP'}`,
        glyph: ctx.outcome === 'DOWN' ? 'down' : 'up',
      }

    case 'SETTLED':
      return { tone: 'neutral', label: 'SETTLED', glyph: 'check' }

    case 'VOIDED':
      // We could not settle this market; the refund journal has not committed
      // yet. `down` rather than `danger` — nobody lost anything.
      return { tone: 'down', label: 'VOIDED · refunding' }

    case 'REFUNDED':
      // Terminal. Every position returned at cost basis, every taker fee
      // reversed — the `VOIDED ▸ REFUNDED` edge of D6.
      return { tone: 'neutral', label: 'VOIDED · refunded at entry', glyph: 'check' }

    case 'CANCELLED':
      // Only reachable from `SCHEDULED`/`FIXING`, so it never implies a lost
      // position: no orders were ever accepted.
      return { tone: 'neutral', label: 'CANCELLED' }
  }
  return assertNever(state)
}

function assertNever(state: never): never {
  throw new Error(`<MarketStatusPill> has no rendering for ${String(state)}`)
}
